<script setup lang="ts">
import {
  Pagination,
  PaginationEllipsis,
  PaginationFirst,
  PaginationLast,
  PaginationList,
  PaginationListItem,
  PaginationNext,
  PaginationPrev
} from "@/components/ui/pagination"
import { Button } from "@/components/ui/button"
import Separator from "@/components/ui/separator/Separator.vue"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select"

import { computed, ref, watch } from "vue"
import { ChevronRight } from "lucide-vue-next"
import { useQuery, keepPreviousData } from "@tanstack/vue-query"
import { useRoute } from "vue-router"

import {  listAuthors, getLocalizedAbstract } from "@/lib/article.ts"

import { getArticles } from "@/models/article"

const route = useRoute()

const currentPage = ref(1)
const limit = ref(10)
const selectedLimit = ref("10")
const params = computed(() => ({
  ...route.query,
  page: currentPage.value.toString(),
  limit: limit.value.toString()
}))

const { data, isFetching, isPlaceholderData } = useQuery({
  queryKey: ["articles", params],
  queryFn: ({ signal }) =>
    getArticles({
      query: params.value,
      signal: signal,
      paginate: true
    }),
  placeholderData: keepPreviousData,
  staleTime: 10 * 60 * 1000 // 10 minutes
})

const pages = computed(() => (data.value ? data.value.pages : 1))

watch(selectedLimit, (newValue) => {
  currentPage.value = 1
  limit.value = parseInt(newValue)
})

const prevPage = () => {
  if (!isFetching.value) {
    currentPage.value = Math.max(currentPage.value - 1, 1)
  }
}
const nextPage = () => {
  if (!isFetching.value && !isPlaceholderData.value) {
    currentPage.value = currentPage.value + 1
  }
}

</script>

<template>
  <div class="container flex flex-col gap-4 pb-10">
    <div class="title">{{ $t("search_results.title") }}</div>

    <div class="flex flex-1 flex-col gap-1 p-1">
      <router-link
        :to="{ name: 'show_article_path', params: { id: article.id } }"
        class="flex flex-col gap-1 border border-gray-300 bg-white p-2 mb-3 transition-all duration-500 hover:bg-gray-100"
        v-for="article in data.data" v-if="data"
      >
        <div class="flex-0 flex text-primary">
            <div class="flex-1">
              [{{ $t("commons.year") }} {{ article.publication_year }} - {{ $t("commons.number") }} {{ article.publication_number }}] - {{ article.title }}
            </div>

            <ChevronRight />
          </div>

        <Separator />

        <div class="flex-0 text-gray-600 p-1" v-if="article?.authors?.length">
          {{ $t("commons.by") }} <span class="font-semibold">{{ listAuthors(article.authors) }}</span>
        </div>

        <div class="line-clamp-2 flex-1" v-if="getLocalizedAbstract(article)">{{ getLocalizedAbstract(article) }}</div>
      </router-link>
    </div>

    <div class="flex-0 flex flex-col items-center justify-center gap-4 md:flex-row" v-if="pages">
      <Pagination :total="pages * limit" :sibling-count="1" show-edges :default-page="1" :items-per-page="limit">
        <PaginationList v-slot="{ items }" class="flex items-center gap-1">
          <PaginationFirst @click="currentPage = 1" />
          <PaginationPrev @click="prevPage" />

          <template v-for="(item, index) in items">
            <PaginationListItem v-if="item.type === 'page'" :key="index" :value="item.value" as-child>
              <Button
                class="h-9 w-9 p-0"
                :variant="item.value === currentPage ? 'default' : 'outline'"
                @click="currentPage = item.value">
                {{ item.value }}
              </Button>
            </PaginationListItem>
            <PaginationEllipsis v-else :key="item.type" :index="index" />
          </template>

          <PaginationNext @click="nextPage" />
          <PaginationLast @click="currentPage = pages" />
        </PaginationList>
      </Pagination>

      <div class="flex items-center gap-2">
        {{ $t("search_results.articles_step") }}
        <Select v-model="selectedLimit">
          <select-trigger class="w-[100px] bg-white">
            <select-value />
          </select-trigger>

          <select-content>
            <select-group>
              <select-label>{{ $t("search_results.articles_step") }}</select-label>
              <select-item value="10"> 10 </select-item>
              <select-item value="20"> 20 </select-item>
              <select-item value="50"> 50 </select-item>
            </select-group>
          </select-content>
        </Select>
      </div>
    </div>
  </div>
</template>
